import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Check, Crown, Star, Heart, Eye, RotateCcw, Sparkles,
  Filter, CheckCheck, MessageCircle, Video, Lock
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const serif = { fontFamily: "'Playfair Display', serif" };
const mono = { fontFamily: "'JetBrains Mono', monospace" };
const NAVY = "#0A2A5A";
const GOLD = "#F9AB0D";
const CREAM = "#F9F6F2";

const SubscriptionPage = () => {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [startingTrial, setStartingTrial] = useState(false);
  const [billingCycle, setBillingCycle] = useState("monthly");
  const [subscriptionInfo, setSubscriptionInfo] = useState(null);
  const [trialStatus, setTrialStatus] = useState(null);

  useEffect(() => {
    fetchSubscriptionInfo();
    fetchTrialStatus();
  }, []);

  const fetchSubscriptionInfo = async () => {
    try {
      const response = await api.getMySubscription();
      setSubscriptionInfo(response.data);
    } catch (error) {
      console.error("Failed to load subscription info");
    } finally {
      setLoading(false);
    }
  };

  const fetchTrialStatus = async () => {
    try {
      const response = await api.get("/subscriptions/trial-status");
      setTrialStatus(response.data);
    } catch (error) {
      console.error("Failed to load trial status");
    }
  };

  const handleStartTrial = async () => {
    setStartingTrial(true);
    try {
      await api.post("/subscriptions/start-trial");
      toast.success("🎉 Your 7-day Recovery+ trial has started!");
      await refreshUser();
      await fetchSubscriptionInfo();
      await fetchTrialStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start trial");
    } finally {
      setStartingTrial(false);
    }
  };

  const handleUpgrade = async (tier) => {
    if (tier === "free") {
      toast.info("You're already on the free plan");
      return;
    }

    setUpgrading(true);
    try {
      const lookupKey = `${tier}_${billingCycle}`;
      const response = await api.createCheckout(lookupKey, window.location.origin);
      window.location.href = response.data.checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to start checkout. Please try again.");
      setUpgrading(false);
    }
  };

  const tiers = [
    {
      id: "free",
      name: "Free",
      price_monthly: 0,
      price_yearly: 0,
      icon: Heart,
      tagline: "Start your journey",
      features: [
        { text: "3 curated matches per day", icon: Heart, included: true },
        { text: "View compatibility scores", icon: Star, included: true },
        { text: "Guided conversation prompts", icon: MessageCircle, included: true },
        { text: "10 messages per match", icon: Lock, included: true, note: "upgrade for unlimited" },
        { text: "See who liked you (blurred)", icon: Eye, included: true, note: "upgrade to reveal" },
        { text: "Super Likes", icon: Sparkles, included: false },
      ],
      highlight: false
    },
    {
      id: "recovery_plus",
      name: "Recovery+",
      price_monthly: 9.99,
      price_yearly: 79.99,
      icon: Star,
      tagline: "Most popular choice",
      features: [
        { text: "10 curated matches per day", icon: Heart, included: true },
        { text: "Unlimited messaging", icon: MessageCircle, included: true },
        { text: "See who liked you (full profiles!)", icon: Eye, included: true, isNew: true },
        { text: "Like Back to instantly match", icon: Heart, included: true, isNew: true },
        { text: "Second Chance (revisit passed)", icon: RotateCcw, included: true },
        { text: "Read receipts & typing indicators", icon: CheckCheck, included: true },
        { text: "Super Likes (3/day)", icon: Sparkles, included: false, note: "Elite only" },
      ],
      highlight: true,
      badge: "Best Value"
    },
    {
      id: "recovery_elite",
      name: "Recovery Elite",
      price_monthly: 19.99,
      price_yearly: 159.99,
      icon: Crown,
      tagline: "The complete experience",
      features: [
        { text: "15 curated matches per day", icon: Heart, included: true },
        { text: "Everything in Recovery+", icon: Check, included: true },
        { text: "Super Likes (3/day)", icon: Sparkles, included: true, isNew: true },
        { text: "Stand out & get noticed first!", icon: Star, included: true, isNew: true },
        { text: "Advanced filters (18+ options)", icon: Filter, included: true },
        { text: "Video calls", icon: Video, included: true },
        { text: "Exclusive Elite badge", icon: Crown, included: true },
      ],
      highlight: false
    }
  ];

  const currentTier = user?.subscription_tier || "free";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: NAVY }} data-testid="subscription-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent" style={{ borderColor: GOLD, borderTopColor: "transparent" }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-16" style={{ backgroundColor: NAVY }} data-testid="subscription-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-8">

        {/* Header */}
        <div className="flex items-center gap-3 pt-6">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-full text-white transition-colors hover:bg-white/10"
            data-testid="back-btn"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
        </div>

        <div className="text-center pt-4 pb-8">
          <p className="text-xs uppercase tracking-[0.25em] font-bold mb-3" style={{ ...mono, color: GOLD }}>
            Recovery+ Membership
          </p>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight mb-4" style={serif}>
            Upgrade your experience.
          </h1>
          <p className="text-sm max-w-md mx-auto" style={{ color: "rgba(255,255,255,0.65)" }}>
            More matches, deeper connections — built for the way recovery actually works.
          </p>

          {/* Current plan pill */}
          <div className="inline-flex items-center gap-2 rounded-full px-5 py-2 mt-6" style={{ backgroundColor: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.15)" }} data-testid="current-plan-pill">
            {currentTier === "free" && <Heart className="w-4 h-4 text-white" />}
            {(currentTier === "recovery_plus" || currentTier === "gold") && <Star className="w-4 h-4" style={{ color: GOLD }} />}
            {(currentTier === "recovery_elite" || currentTier === "platinum") && <Crown className="w-4 h-4" style={{ color: GOLD }} />}
            <span className="text-sm text-white/70">Current plan:</span>
            <span className="text-sm font-bold text-white">
              {currentTier === "free" ? "Free" :
               (currentTier === "recovery_plus" || currentTier === "gold") ? "Recovery+" :
               "Recovery Elite"}
            </span>
          </div>

          {/* Usage stats */}
          {subscriptionInfo?.usage && (
            <div className="mt-4 flex justify-center gap-8 text-sm" data-testid="usage-stats">
              <div>
                <span style={{ color: "rgba(255,255,255,0.55)" }}>Daily Matches: </span>
                <span className="font-bold" style={{ color: GOLD }}>
                  {subscriptionInfo.usage.matches_remaining === -1
                    ? "∞"
                    : `${subscriptionInfo.usage.matches_remaining || 0}/${subscriptionInfo.usage.matches_limit || 3}`}
                </span>
              </div>
              <div>
                <span style={{ color: "rgba(255,255,255,0.55)" }}>Mutual: </span>
                <span className="font-bold" style={{ color: GOLD }}>
                  {subscriptionInfo.usage.mutual_matches || 0}
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Free Trial Banner */}
        {trialStatus?.trial_available && currentTier === "free" && (
          <div className="max-w-2xl mx-auto mb-10">
            <div className="rounded-3xl p-6 sm:p-8 shadow-2xl" style={{ backgroundColor: GOLD }}>
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(10,42,90,0.15)" }}>
                  <Star className="w-6 h-6" style={{ color: NAVY }} />
                </div>
                <div>
                  <h3 className="font-extrabold text-lg" style={{ color: NAVY }}>Try Recovery+ free for 7 days</h3>
                  <p className="text-sm" style={{ color: "rgba(10,42,90,0.7)" }}>No credit card required</p>
                </div>
              </div>
              <ul className="text-sm space-y-1.5 mb-5" style={{ color: "rgba(10,42,90,0.85)" }}>
                <li className="flex items-center gap-2"><Check className="w-4 h-4" /> 10 curated matches per day</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4" /> See who expressed interest</li>
                <li className="flex items-center gap-2"><Check className="w-4 h-4" /> Unlimited messaging</li>
              </ul>
              <button
                onClick={handleStartTrial}
                disabled={startingTrial}
                className="w-full rounded-full py-3.5 font-bold text-white transition-transform hover:scale-[1.01] disabled:opacity-60"
                style={{ backgroundColor: NAVY }}
                data-testid="start-trial-btn"
              >
                {startingTrial ? "Starting..." : "Start My Free Trial"}
              </button>
            </div>
          </div>
        )}

        {/* Active Trial Banner */}
        {trialStatus?.is_trial && trialStatus?.days_remaining > 0 && (
          <div className="max-w-2xl mx-auto mb-10">
            <div className="rounded-3xl p-6 shadow-xl bg-white" data-testid="active-trial-banner">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: "rgba(249,171,13,0.15)" }}>
                    <Star className="w-6 h-6" style={{ color: GOLD }} />
                  </div>
                  <div>
                    <h3 className="font-extrabold" style={{ color: NAVY }}>Recovery+ Trial Active</h3>
                    <p className="text-sm text-slate-500">
                      {trialStatus.days_remaining} day{trialStatus.days_remaining !== 1 ? 's' : ''} remaining
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => handleUpgrade("recovery_plus")}
                  className="rounded-full px-6 py-2.5 font-bold text-sm transition-transform hover:scale-[1.02]"
                  style={{ backgroundColor: GOLD, color: NAVY }}
                >
                  Subscribe Now
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Billing Toggle */}
        <div className="flex justify-center pb-10">
          <div className="rounded-full p-1 flex" style={{ backgroundColor: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.12)" }}>
            <button
              onClick={() => setBillingCycle("monthly")}
              className="px-7 py-2.5 rounded-full text-sm font-bold transition-colors"
              style={billingCycle === "monthly" ? { backgroundColor: GOLD, color: NAVY } : { color: "rgba(255,255,255,0.7)" }}
              data-testid="billing-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle("yearly")}
              className="px-7 py-2.5 rounded-full text-sm font-bold transition-colors"
              style={billingCycle === "yearly" ? { backgroundColor: GOLD, color: NAVY } : { color: "rgba(255,255,255,0.7)" }}
              data-testid="billing-yearly"
            >
              Yearly <span className="ml-1" style={{ color: billingCycle === "yearly" ? NAVY : GOLD }}>Save 33%</span>
            </button>
          </div>
        </div>

        {/* Plans */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start pb-12">
          {tiers.map((tier) => {
            const Icon = tier.icon;
            const isCurrent = currentTier === tier.id ||
              (currentTier === "gold" && tier.id === "recovery_plus") ||
              (currentTier === "platinum" && tier.id === "recovery_elite") ||
              (currentTier === "recovery_elite" && tier.id === "recovery_elite");
            const price = billingCycle === "monthly" ? tier.price_monthly : tier.price_yearly;

            return (
              <div
                key={tier.id}
                className="rounded-3xl overflow-hidden bg-white transition-transform hover:-translate-y-1"
                style={{
                  boxShadow: tier.highlight ? "0 24px 60px rgba(0,0,0,0.35)" : "0 12px 32px rgba(0,0,0,0.25)",
                  border: tier.highlight ? `2px solid ${GOLD}` : "1px solid rgba(255,255,255,0.1)"
                }}
                data-testid={`tier-${tier.id}`}
              >
                {tier.badge ? (
                  <div className="text-center py-2 text-xs font-bold uppercase tracking-[0.2em]" style={{ ...mono, backgroundColor: GOLD, color: NAVY }}>
                    ★ {tier.badge}
                  </div>
                ) : (
                  <div className="py-2 hidden md:block">&nbsp;</div>
                )}

                <div className="p-7">
                  <div className="flex items-center gap-3 mb-5">
                    <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: tier.id === "free" ? "rgba(10,42,90,0.06)" : "rgba(249,171,13,0.15)" }}>
                      <Icon className="w-6 h-6" style={{ color: tier.id === "free" ? NAVY : GOLD }} />
                    </div>
                    <div>
                      <h3 className="text-2xl" style={{ ...serif, color: NAVY }}>{tier.name}</h3>
                      <p className="text-xs text-slate-500">{tier.tagline}</p>
                    </div>
                  </div>

                  <div className="flex items-baseline gap-2 mb-6">
                    <p className="text-4xl font-extrabold tracking-tight" style={{ color: NAVY }}>
                      {price === 0 ? "Free" : `$${price}`}
                    </p>
                    {price > 0 && (
                      <p className="text-sm text-slate-400">/ {billingCycle === "monthly" ? "month" : "year"}</p>
                    )}
                  </div>

                  <ul className="space-y-3 mb-7">
                    {tier.features.map((feature, idx) => {
                      const FeatureIcon = feature.icon;
                      return (
                        <li key={idx} className={`flex items-start gap-3 ${!feature.included ? 'opacity-40' : ''}`}>
                          <FeatureIcon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: feature.included ? GOLD : "#94a3b8" }} />
                          <span className="text-sm flex-1" style={{ color: "#334155" }}>
                            {feature.text}
                            {feature.note && (
                              <span className="text-xs text-slate-400"> ({feature.note})</span>
                            )}
                          </span>
                          {feature.isNew && (
                            <span className="px-1.5 py-0.5 text-[10px] font-bold rounded" style={{ backgroundColor: "rgba(249,171,13,0.15)", color: "#b07500" }}>
                              NEW
                            </span>
                          )}
                          {!feature.included && !feature.isNew && (
                            <Lock className="w-3 h-3 mt-1 text-slate-300" />
                          )}
                        </li>
                      );
                    })}
                  </ul>

                  <button
                    onClick={() => handleUpgrade(tier.id)}
                    disabled={isCurrent || upgrading}
                    className="w-full rounded-full py-3.5 font-bold text-sm transition-transform hover:scale-[1.02] disabled:hover:scale-100 disabled:cursor-not-allowed"
                    style={
                      isCurrent
                        ? { backgroundColor: "rgba(10,42,90,0.08)", color: "#94a3b8" }
                        : tier.id === "free"
                          ? { backgroundColor: "rgba(10,42,90,0.08)", color: NAVY }
                          : tier.id === "recovery_elite"
                            ? { backgroundColor: NAVY, color: "#fff" }
                            : { backgroundColor: GOLD, color: NAVY }
                    }
                    data-testid={`upgrade-${tier.id}`}
                  >
                    {isCurrent ? "Current Plan" : tier.id === "free" ? "Free Plan" : tier.id === "recovery_plus" ? "Get Recovery+" : "Go Elite"}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Why Upgrade */}
        <div className="max-w-2xl mx-auto pb-8">
          <div className="rounded-3xl p-6 sm:p-8" style={{ backgroundColor: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.1)" }}>
            <h4 className="font-bold text-white mb-3 flex items-center gap-2">
              <Sparkles className="w-4 h-4" style={{ color: GOLD }} />
              Why upgrade?
            </h4>
            <ul className="text-sm space-y-1.5" style={{ color: "rgba(255,255,255,0.7)" }}>
              <li>• <strong className="text-white">More matches</strong> = more chances to find your person</li>
              <li>• <strong className="text-white">See who likes you</strong> = skip the guessing game</li>
              <li>• <strong className="text-white">Unlimited messages</strong> = no barriers to connection</li>
            </ul>
          </div>
        </div>

        {/* Note */}
        <div className="text-center">
          <p className="text-sm mb-1" style={{ color: "rgba(255,255,255,0.55)" }}>
            Cancel anytime. No hidden fees.
          </p>
          <p className="text-xs" style={{ color: "rgba(255,255,255,0.35)" }}>
            Secure payments powered by Stripe.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionPage;
